/**
 * "No case panel configured" empty state, upgraded: before asking the recruiter
 * to type interviewers, try to READ them from the role's hiring kick-off sheet
 * (proxy POST /panel-suggest — hiring_plan → kick-off doc → heuristic parse →
 * directory). The result is only ever a PRE-FILL for the panel modal; nothing
 * is saved until the recruiter confirms there.
 *
 * Access model: the document is exported AS THE SIGNED-IN USER via the same
 * Google grant the Requests tab uses (X-Gmail-Token, drive.readonly). With a
 * standing grant the token mints silently on mount; before the first-ever
 * grant we show a connect button rather than auto-firing a popup.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { Spinner } from '@finn-auto/design-system/atoms/spinner';
import type { CasePanelSuggestion } from '../../services/taActionBoardApi';
import type { RoleBoard } from '../../types/taActionBoard';
import { fullNameFromEmail } from './format';
import { useKickoffSuggestion } from './useKickoffSuggestion';

export type PanelSuggestion = CasePanelSuggestion;

export function NoPanelState({
  role,
  myEmail,
  onConfigure,
}: {
  role: RoleBoard;
  myEmail: string;
  /** Opens the edit-panel modal, optionally pre-filled from the kick-off sheet */
  onConfigure: (suggestion: PanelSuggestion | null) => void;
}) {
  // The Google-token dance and the fetch live in the shared hook — the FINAL
  // round reads its own section of the same sheet the same way.
  const { state, connectAndFetch, setLoading, connecting } =
    useKickoffSuggestion<PanelSuggestion>(role.postingId, 'case', myEmail);

  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center">
      <p className="text-sm font-semibold text-gray-700">No case panel configured</p>

      {state.phase === 'loading' ? (
        <p className="flex items-center gap-2 text-xs text-gray-500">
          <Spinner className="h-3 w-3" /> Reading the kick-off sheet for a panel suggestion…
        </p>
      ) : state.phase === 'found' ? (
        <>
          <p className="text-xs text-gray-600">
            From the{' '}
            {state.kickoffUrl ? (
              <a href={state.kickoffUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                kick-off sheet ↗
              </a>
            ) : (
              'kick-off sheet'
            )}
            : <span className="font-semibold text-gray-800">{state.suggestion.interviewers.map(fullNameFromEmail).join(' + ')}</span>
            {state.suggestion.minutes ? ` · ${state.suggestion.minutes} min` : ''}
          </p>
          {state.unresolved.length > 0 && (
            <p className="text-xs text-gray-400">
              Could not match: {state.unresolved.join(', ')} — add them by hand if they belong on the panel.
            </p>
          )}
          <Button size="sm" className="mt-1" onClick={() => onConfigure(state.suggestion)}>
            Review &amp; save panel
          </Button>
        </>
      ) : state.phase === 'connect' ? (
        <>
          <p className="text-xs text-gray-500">
            Connect your Google account and the board pre-fills the panel from the role's kick-off
            sheet. Configuring by hand works too.
          </p>
          <div className="mt-1 flex flex-wrap justify-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setLoading();
                void connectAndFetch();
              }}
              loading={connecting}
            >
              Suggest from kick-off sheet
            </Button>
            <Button size="sm" onClick={() => onConfigure(null)}>
              Configure panel
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            {state.reason}
            {state.kickoffUrl && (
              <>
                {' '}
                <a href={state.kickoffUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                  Open the kick-off sheet ↗
                </a>
              </>
            )}
          </p>
          <Button size="sm" className="mt-1" onClick={() => onConfigure(null)}>
            Configure panel
          </Button>
        </>
      )}
    </div>
  );
}
